import { useState, useEffect } from 'react';
import { adminAPI } from '../../lib/api';
import AdminLayout from '../../components/AdminLayout';
import { Badge } from '../../components/ui/badge';
import { ChevronLeft, ChevronRight, DollarSign } from 'lucide-react';

const STATUS_STYLES = {
  completed: 'bg-[#FF7A3D]/10 text-[#FF7A3D] border-[#FF7A3D]/20',
  paid: 'bg-[#FF7A3D]/10 text-[#FF7A3D] border-[#FF7A3D]/20',
  initiated: 'bg-[#ffbd2e]/10 text-[#ffbd2e] border-[#ffbd2e]/20',
  pending: 'bg-[#ffbd2e]/10 text-[#ffbd2e] border-[#ffbd2e]/20',
  failed: 'bg-[#ff003c]/10 text-[#ff003c] border-[#ff003c]/20',
  expired: 'bg-[#555]/10 text-[#aaa] border-[#555]/20',
};

export default function AdminPayments() {
  const [transactions, setTransactions] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const limit = 25;

  useEffect(() => {
    adminAPI.payments(page * limit, limit).then(res => {
      setTransactions(res.data.transactions);
      setTotal(res.data.total);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [page]);

  const totalRevenue = transactions.filter(t => ['paid', 'completed'].includes(t.payment_status)).reduce((sum, t) => sum + (t.amount || 0), 0);

  return (
    <AdminLayout>
      <div data-testid="admin-payments-page">
        <div className="flex items-center justify-between mb-8">
          <div>
            <p className="font-mono text-xs text-[#ff003c] tracking-widest uppercase mb-2">FINANCIAL</p>
            <h1 className="font-heading text-3xl font-bold text-white tracking-tight">Payments ({total})</h1>
          </div>
          <div className="card-cyber p-4 rounded-sm">
            <DollarSign className="w-4 h-4 text-[#FF7A3D] mb-1" />
            <p className="font-heading text-xl font-bold text-[#FF7A3D]">${totalRevenue.toFixed(2)}</p>
            <p className="font-mono text-[10px] text-[#aaa]">PAGE TOTAL</p>
          </div>
        </div>

        <div className="card-cyber rounded-sm overflow-hidden" data-testid="payments-table">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/5">
                  <th className="text-left font-mono text-[10px] text-[#aaa] tracking-widest p-3">TXN ID</th>
                  <th className="text-left font-mono text-[10px] text-[#aaa] tracking-widest p-3">USER</th>
                  <th className="text-left font-mono text-[10px] text-[#aaa] tracking-widest p-3">PACKAGE</th>
                  <th className="text-left font-mono text-[10px] text-[#aaa] tracking-widest p-3">AMOUNT</th>
                  <th className="text-left font-mono text-[10px] text-[#aaa] tracking-widest p-3">CREDITS</th>
                  <th className="text-left font-mono text-[10px] text-[#aaa] tracking-widest p-3">STATUS</th>
                  <th className="text-left font-mono text-[10px] text-[#aaa] tracking-widest p-3">DATE</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={7} className="p-8 text-center"><div className="w-6 h-6 border-2 border-[#FF7A3D] border-t-transparent rounded-full animate-spin mx-auto" /></td></tr>
                ) : transactions.length === 0 ? (
                  <tr><td colSpan={7} className="p-8 text-center font-mono text-xs text-[#aaa]">No transactions yet</td></tr>
                ) : transactions.map(t => (
                  <tr key={t.transaction_id || t.session_id} className="border-b border-white/[0.03] hover:bg-white/[0.02]">
                    <td className="p-3 font-mono text-[10px] text-[#888]">{t.transaction_id || '-'}</td>
                    <td className="p-3 font-mono text-xs text-white">{t.user_id}</td>
                    <td className="p-3 font-mono text-xs text-[#00E5C1] uppercase">{t.package_id || '-'}</td>
                    <td className="p-3 font-mono text-xs text-white">${(t.amount || 0).toFixed(2)}</td>
                    <td className="p-3 font-mono text-xs text-[#FF7A3D]">{t.credits || 0}</td>
                    <td className="p-3">
                      <Badge className={`font-mono text-[10px] uppercase ${STATUS_STYLES[t.payment_status] || STATUS_STYLES.pending}`}>
                        {t.payment_status || 'UNKNOWN'}
                      </Badge>
                    </td>
                    <td className="p-3 font-mono text-[10px] text-[#aaa]">{t.created_at ? new Date(t.created_at).toLocaleDateString() : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between p-3 border-t border-white/5">
            <span className="font-mono text-[10px] text-[#aaa]">Page {page + 1} of {Math.max(1, Math.ceil(total / limit))}</span>
            <div className="flex gap-2">
              <button onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0} className="text-[#aaa] hover:text-white disabled:opacity-30"><ChevronLeft className="w-4 h-4" /></button>
              <button onClick={() => setPage(page + 1)} disabled={(page + 1) * limit >= total} className="text-[#aaa] hover:text-white disabled:opacity-30"><ChevronRight className="w-4 h-4" /></button>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
